import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TrackingPageCouponCodeRules } from '@pages/checkout-app/checkout-layout/main/components/tracking-page/components/tracking-page-widgets/tracking-page-coupon-widget/config';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shop } from '@services/shops/shop.model';
import { getSignOfCurrency } from '@utils/localization-utils';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { v4 as uuidv4 } from 'uuid';

const CouponTypeOptions = [
  {
    label: 'Fixed',
    value: 'fixed',
  },
  {
    label: 'Percentage',
    value: 'percentage',
  },
];

const DefaultCouponRule = {
  entity: 'numOfDaysLate',
  entityOperator: 'is_more_than_or_equal_to',
  entityParams: { numOfDaysLateValue: '1' },
};

@Component({
  selector: 'tracking-page-coupon-widget',
  templateUrl: './tracking-page-coupon-widget.component.html',
  styleUrls: ['./tracking-page-coupon-widget.component.scss', '../../../tracking-page.component.scss'],
  standalone: false,
})
export class TrackingPageCouponWidgetComponent implements OnInit, OnChanges {
  @Input() formGroup: UntypedFormGroup;
  @Input() shop: Shop;
  @Input() loadedValues: ShopPlugin.TrackingPage.CouponCodesWidget[];
  @Output() onPreviewChange: EventEmitter<number> = new EventEmitter<number>();
  readonly trackingPageCouponCodeRules = TrackingPageCouponCodeRules;
  readonly formArrayFieldName = 'rules';
  readonly couponTypeOptions = CouponTypeOptions;
  couponPreviewIndex = 0;
  entities: string[];

  constructor(private rulesDynamicFormService: RulesDynamicFormService) {}

  ngOnInit(): void {
    this.entities = this.trackingPageCouponCodeRules.map((r) => r.entity);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadedValues?.currentValue) {
      this.loadCouponCodesFormArray();
    }
    // else {
    //   this.couponCodesFormArray.length == 0 && this.addCouponCode();
    // }
  }

  loadCouponCodesFormArray() {
    this.loadedValues?.forEach((couponCode: ShopPlugin.TrackingPage.CouponCodesWidget) => {
      this.addCouponCode(couponCode);
    });
  }

  get widgetsFormGroup(): UntypedFormGroup {
    return this.formGroup.get('widgets') as UntypedFormGroup;
  }

  get couponCodesFormArray(): UntypedFormArray {
    return this.widgetsFormGroup.get('couponCodes') as UntypedFormArray;
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.couponCodesFormArray.controls, event.previousIndex, event.currentIndex);
    this.couponCodesFormArray.controls.forEach((control, index) => {
      control.get('priority').setValue(index);
    });
    this.formGroup.markAsDirty();
  }

  addCouponCode(loadedValues?: ShopPlugin.TrackingPage.CouponCodesWidget) {
    const rules = loadedValues ? loadedValues?.rules : [DefaultCouponRule];
    const couponCodesForm = new UntypedFormGroup({
      internalName: new UntypedFormControl(loadedValues?.internalName ?? null),
      title: new UntypedFormControl(loadedValues?.title ?? "Oops! your order is late, here's a 10% off coupon code"),
      rules: this.rulesDynamicFormService.initRulesFormArray(rules, this.trackingPageCouponCodeRules),
      description: new UntypedFormControl(
        loadedValues?.description ?? 'Here’s a 10% coupon code as a thank you for your patience',
        [Validators.required],
      ),
      type: new UntypedFormControl(loadedValues?.type ?? 'fixed', [Validators.required]),
      value: new UntypedFormControl(loadedValues?.value ?? 10, [Validators.required]),
      expiresInDays: new UntypedFormControl(loadedValues?.expiresInDays ?? null),
      combinesWithProductDiscounts: new UntypedFormControl(loadedValues?.combinesWithProductDiscounts ?? false),
      combinesWithOrderDiscounts: new UntypedFormControl(loadedValues?.combinesWithOrderDiscounts ?? false),
      combinesWithShippingDiscounts: new UntypedFormControl(loadedValues?.combinesWithShippingDiscounts ?? false),
      priority: new UntypedFormControl(loadedValues?.priority ?? this.couponCodesFormArray.length),
      templateToken: new UntypedFormControl(loadedValues?.templateToken ?? uuidv4()),
    });
    this.couponCodesFormArray.push(couponCodesForm);
  }

  removeCouponCode(index: number) {
    this.couponCodesFormArray.removeAt(index);
    if (this.couponCodesFormArray.length > 1) {
      this.onPreviewChange.next(0);
    } else {
      this.onPreviewChange.next(-1);
    }

    this.formGroup.markAsDirty();
  }

  previewCouponCode(event: Event, index: number) {
    event.stopPropagation();
    this.couponPreviewIndex = index;
    this.onPreviewChange.next(index);
  }

  protected readonly TrackingPageCouponCodeRules = TrackingPageCouponCodeRules;
  protected readonly FormGroup = UntypedFormGroup;
  protected readonly getSignOfCurrency = getSignOfCurrency;
}
